import React, { useEffect } from 'react';
import { Alert, Grid } from '@mui/material';
import CardMember from './components/CardMember';
import StaticBanner from './components/StaticBanner';
import RecentTransactions from './components/RecentTransactions';
import { GetFamiliplusProfile } from 'services/profile/ProfileService';
import useApi, { Status } from 'common/hooks/useApi';
import TransactionApi from '../../services/transaction/TransactionService';
import FamiliplusNotMember from './FamiliplusNotMember';
import LoadingIndicator from 'components/common/LoadingIndicator';
import { useTranslation } from 'react-i18next';
import useAuth0 from 'common/hooks/useAuth0';
import { Helmet } from 'react-helmet';

const cardColWidth = '327px';
const colSpacing = '24px';

const FamiliplusSummaryPage = (): JSX.Element => {
  const { t } = useTranslation('component');
  const { user } = useAuth0();
  const [fetchTransactions, transactions, fetchingTransactionStatus] = useApi(TransactionApi.fetchMany);
  const [fetchTransactionTypes, transactionTypes, fetchingTransTypeStatus] = useApi(TransactionApi.fetchTypes);
  const [fetchProfile, profile, fetchingProfileStatus] = useApi(GetFamiliplusProfile);
  const isDataLoading = fetchingProfileStatus === Status.Pending;
  const hasFetchDataFailed = fetchingProfileStatus === Status.Rejected;
  const isDataResolved = fetchingProfileStatus === Status.Resolved;

  useEffect(() => {
    if (user?.session_id) {
      fetchProfile(user!.session_id);
    }
  }, [fetchProfile, user]);

  useEffect(() => {
    if (profile?.Card?.CardNumber) {
      fetchTransactions(user!.session_id, { nbResult: 100 });
      fetchTransactionTypes();
    }
  }, [fetchTransactions, fetchTransactionTypes, profile, user]);

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>{t('MetaTag.FamiliplusTitle')}</title>
        <meta name="description" content={t('MetaTag.FamiliplusDescription').toString()} />
      </Helmet>
      {isDataLoading && <LoadingIndicator />}

      {hasFetchDataFailed && (
        <Alert id="fetchAlert" severity="error" sx={{ mb: '16px' }}>
          {t('Errors.GenericError')}
        </Alert>
      )}

      {isDataResolved &&
        (profile?.Card?.CardNumber ? (
          <Grid container spacing={colSpacing}>
            <Grid item xs={12} md>
              <RecentTransactions
                transactions={transactions}
                transactionTypes={transactionTypes}
                fetchingTransactionStatus={fetchingTransactionStatus}
                fetchingTransTypeStatus={fetchingTransTypeStatus}
              />
            </Grid>

            <Grid item sx={{ width: { xs: '100%', md: `calc(${cardColWidth} + ${colSpacing})` } }}>
              <CardMember profile={profile!} transactions={transactions} />
            </Grid>

            <Grid item xs={12} paddingTop={'0 !important'}>
              <StaticBanner />
            </Grid>
          </Grid>
        ) : (
          <FamiliplusNotMember />
        ))}
    </>
  );
};

export default FamiliplusSummaryPage;
